/**
 * Consent gate for any third-party analytics/tracking SDK (currently
 * Plausible — see `mem://features/observability-stack.md`).
 *
 * The SDK init code MUST call `hasConsent()` before booting — and the
 * banner UI must write to `CONSENT_KEY` via `setConsent()` before any
 * tracking can fire.
 *
 * Default is reject-all: no entry in localStorage → `hasConsent()` returns
 * false. This matches the GDPR/UK-PECR "opt-in, no implied consent" posture
 * described in the privacy policy and /cookies page.
 */


export const CONSENT_KEY = "excerptus.analytics.consent";

export type ConsentValue = "granted" | "denied";

const isBrowser = (): boolean => typeof window !== "undefined" && typeof window.localStorage !== "undefined";

/** Returns the stored consent value, or null if the user hasn't decided yet. */
export const getConsent = (): ConsentValue | null => {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    return raw === "granted" || raw === "denied" ? raw : null;
  } catch {
    return null;
  }
};

/** True only when the user has explicitly granted consent. Default = false. */
export const hasConsent = (): boolean => getConsent() === "granted";

/** Persist a consent decision. Future analytics SDKs read this before init. */
export const setConsent = (value: ConsentValue): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // localStorage can throw in private mode / when full — fail closed.
  }
};

/** Clear the stored decision (back to "undecided" → reject-all). */
export const clearConsent = (): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    // ignore
  }
};
